import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchInvoiceTerms, saveInvoiceTerms, type InvoiceTermsResponse } from "../../expenses/services/expensesService";
import { ErrorBox, Field, Notice, Spinner } from "../../expenses/components/ui";
import { dateTime } from "../../expenses/lib/format";
import { ui } from "../../expenses/theme";

// Plazo de pago de omisión de las facturas a clientes: cuántos días después del
// corte vence una factura. Vale para todo negocio que no tenga un plazo propio
// (CRM → Negocios → editar → Cobro). Lo usan la corrida de corte, los borradores
// del mes y los ingresos esperados. Cambiarlo no mueve el vencimiento de las
// facturas ya emitidas.

export default function PaymentTermsCard() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const tf = (k: string, o?: Record<string, unknown>) => t(`financeSettings.paymentTerms.${k}`, o);
  const [data, setData] = useState<InvoiceTermsResponse | null>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const terms = await fetchInvoiceTerms();
      setData(terms);
      setValue(String(terms.dueDays));
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("financeSettings.errors.load"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const days = Number(value);
  const valid = value.trim() !== "" && Number.isInteger(days) && days >= 0 && days <= (data?.maxDueDays ?? 90);

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    setNotice(null);
    try {
      const terms = await saveInvoiceTerms(days);
      setData(terms);
      setValue(String(terms.dueDays));
      setNotice(tf("saved", { count: terms.dueDays }));
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("financeSettings.errors.save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={c.card} data-testid="payment-terms">
      <h3 className={`text-sm font-semibold ${c.text}`}>{tf("title")}</h3>
      <p className={`mt-1 text-xs ${c.muted}`}>{tf("subtitle")}</p>
      <div className="mt-3 space-y-3">
        <ErrorBox message={error} />
        {notice && <Notice tone="ok">{notice}</Notice>}
        {!data && !error && <Spinner />}
        {data && (
          <>
            <p className={`text-3xl font-bold tabular-nums ${c.text}`} data-testid="payment-terms-current">
              {data.dueDays === 0 ? tf("sameDay") : tf("days", { count: data.dueDays })}
            </p>
            {data.updatedBy && (
              <p className={`text-xs ${c.muted}`}>
                {tf("updatedBy", { by: data.updatedBy })}
                {data.updatedAt ? ` · ${dateTime(data.updatedAt, i18n.language)}` : ""}
              </p>
            )}
            {data.canEdit && (
              <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
                <Field label={tf("field")}>
                  <input
                    type="number"
                    min="0"
                    max={data.maxDueDays}
                    step="1"
                    inputMode="numeric"
                    aria-label={tf("field")}
                    className={c.input}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                </Field>
                <div className="sm:col-span-3">
                  <button type="button" className={c.primary} disabled={busy || !valid || days === data.dueDays} onClick={save}>
                    <i className="ri-save-line mr-1" />
                    {tf("save")}
                  </button>
                  <p className={`mt-1 text-xs ${c.muted}`}>{tf("hint", { max: data.maxDueDays })}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
